import React, { useState, useEffect, useMemo, useRef, useLayoutEffect } from "react";
import { Row, Col, Button, Card, CardBody, Spinner } from "reactstrap";
import { useForm } from "react-hook-form";
import CommonForm from "Components/Common/CommonForm";
import CommonButton from "Components/Common/CommonButton";
import styles from "../../MainPrintListTabs/css/printlabel.module.scss";
import { useMasterStudyService } from "modules/Clinical/StudyDetails/api/useStudyQuery";
import CommonTableIndexWise from "Components/Common/CommonTableIndexWise";
import tableStyles from "../../../../assets/scss/common/table.module.scss";
import sf from "../../../../assets/scss/common/searchFilter.module.scss";
import SimpleBar from "simplebar-react";
import { templateApiService } from "modules/Library/Template/api/template.api";
import DOMPurify from "dompurify";
import Barcode from "react-barcode";
import { createRoot } from "react-dom/client";
import { formatDate, showToastMessage } from "common/service/CommonService";
import { queryClient } from "api/queryClient";
import { useGlobalStudy } from "../../../../context/StudyContext";
import { ICardColumns, ICardFields } from "../helper/Icard.constant";
import { SubjectICardFormValues } from "../helper/Icard.types";
import { useSubjectLabelService } from "modules/PrintLabel/SubjectLabel/api/useSubjectLabelService";
import { PERMISSION_ACTIONS, PERMISSION_MODULES } from "common/constants/PermissionConstants";
import { useVolunteerEnrollmentService } from "modules/Clinical/SubjectEnrollment/api/useVolunteerEnrollment";

const SubjectICardList: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [templateHtml, setTemplateHtml] = useState<string | null>(null);
  const [isTemplateLoading, setIsTemplateLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  const [isFlipped, setIsFlipped] = useState(false);
  const previewRef = useRef<HTMLDivElement>(null);
  const { UpdateLabelPrintMutation } = useSubjectLabelService();

  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm<SubjectICardFormValues>({
    defaultValues: {
      studyid: null,
    },
  });

  const selectedStudyId = watch("studyid");
  const { selectedStudy } = useGlobalStudy();

  useEffect(() => {
    if (selectedStudy) {
      setValue("studyid", Number(selectedStudy.studyid));
    }
  }, [selectedStudy, setValue]);

  // Fetch all studies for the dropdown
  const { DropDownAllQuery: studySelectAllQuery } = useMasterStudyService({ enabledDropDown: true });

  const studyOptions =
    (studySelectAllQuery.data as any)?.data?.map((study: any) => ({
      label: study.studycode,
      value: study.studyid,
    })) || [];

  const selectedStudyLabel =
    studyOptions.find((opt: any) => opt.value === selectedStudyId)?.label || "";

  // Fetch volunteers based on selected study
  const { SelectAllQuery: volunteersQuery } = useVolunteerEnrollmentService({
    studyid: selectedStudyId ? Number(selectedStudyId) : null,
    searchText: searchText,
    idCardPrintStatus: "U",
    filtersubjectstatus: true,
  });

  const apiResponse = volunteersQuery.data as any;
  const totalRecords = apiResponse?.data?.[0]?.totalcount || apiResponse?.totalcount || 0;

  const volunteersData = ((volunteersQuery.data as any)?.data || []).filter(
    (v: any) => {
      if (!v.initials || v.initials.trim() === "") return false;
      const isNumeric = v.subjectno && !isNaN(Number(v.subjectno)) && String(v.subjectno).trim() !== "";
      const status = v.replacementstatus;
      if (isNumeric) {
        return status !== "replaced";
      }
      return status === "replacement";
    }
  );
  // Fetch ID Card Template
  useEffect(() => {
    if (selectedStudyId) {
      fetchTemplate();
    }
    setSelectedRows([]);
    setCurrentIndex(0);
    setSearchText("");
    setIsFlipped(false);
  }, [selectedStudyId]);


  const fetchTemplate = async () => {
    try {
      setIsTemplateLoading(true);
      const res = await templateApiService.getSelectAllData({
        searchtext: "ICARD new style",
        pagenumber: 1,
        pagesize: 10,
        status: "A",
      });
      const templates = res?.data || [];
      const iCardTemplate = templates.find((t: any) => {
        const name = String(t.templatename || t.name || "").toLowerCase();
        return name === "icard new style" || name.includes("icard");
      });

      if (iCardTemplate) {
        setTemplateHtml(iCardTemplate.designtemplate);
      } else if (templates.length > 0) {
        setTemplateHtml(templates[0].designtemplate);
      }
    } catch (error) {
      console.error("Error fetching ID Card template", error);
    } finally {
      setIsTemplateLoading(false);
    }
  };

  const selectedAssignmentIds = useMemo(
    () => selectedRows.map((row) => row.volunteerassignmentid),
    [selectedRows],
  );


  const toggleSelection = (row: any, isChecked: boolean) => {
    setSelectedRows((prev) =>
      isChecked
        ? [...prev, row]
        : prev.filter(
          (r) => r.volunteerassignmentid !== row.volunteerassignmentid,
        ),
    );
    setCurrentIndex(0);
    setIsFlipped(false);
  };



  // Preview Logic
  const previewList = selectedRows.length > 0 ? selectedRows : [];
  const currentSubject = previewList[currentIndex];

  const replacePlaceholders = (template: string, data: Record<string, any>) => {
    return template.replace(/{{(.*?)}}/g, (_, key) => {
      return data[key.trim()] ?? "";
    });
  };

  const mapSubjectToData = (row: any) => {
    const barcodeValue = row.barcode || "123456789";
    const barcodeHtml = `<div class="barcode-target" data-barcode="${barcodeValue}"></div>`;
    const API_FILE_BASE_URL =  sessionStorage.getItem("filebaseUrl") || process.env.API_FILE_BASE_URL

    const fileBaseUrl = API_FILE_BASE_URL;
    

    const defaultAvatar =
      "https://img.freepik.com/premium-vector/vector-flat-illustration-grayscale-avatar-user-profile-person-icon-profile-picture-suitable-social-media-profiles-icons-screensavers-as-templatex9xa_719432-2093.jpg?semt=ais_hybrid&w=740&q=80";
    const imageUrl = row.profileimg
      ? `${fileBaseUrl}/${row.profileimg}`
      : defaultAvatar;

    // Formatted dates
    const dobFormatted = formatDate(row.dob);
    const printedOnFormatted = formatDate(row.printdate || new Date());

    // Generate Period Rows HTML
    let periodRowsHtml = "";
    try {
      if (row.perioddosingdates) {
        const dates = JSON.parse(row.perioddosingdates);
        periodRowsHtml = Object.keys(dates)
          .sort()
          .map((key) => {
            const periodNum = parseInt(key, 10);
            const formattedDate = formatDate(dates[key]);
            return `
              <div class="id_period_cell">
                  <span class="id_label">Period ${periodNum}</span>
                  <span class="id_value">${formattedDate}</span>
              </div>
            `;
          })
          .join("");
      }
    } catch (e) {
      console.error("Error parsing period dosing dates", e);
    }

    const signatureUrl = row.signature
      ? `${fileBaseUrl}/${row.signature}`
      : "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";

    return {
      Barcode: "___BARCODE_MARKER___",
      BarcodeHtml: barcodeHtml,
      PhotoUrl: imageUrl,
      CompanyLogo: "https://adrtatech.com/assets/img/logo-color.png",
      StudyCode: selectedStudyLabel || "N/A",
      SubjectNo: row.subjectno ? (Number(row.subjectno) >= 1 && Number(row.subjectno) <= 9 ? String(row.subjectno).padStart(2, "0") : row.subjectno) : "N/A",
      PeriodRows: periodRowsHtml,
      VolunteerCode: row.volunteercode || "N/A",
      Initial: row.initials || "N/A",
      Dob: dobFormatted,
      PrintedOn: printedOnFormatted,
      VolunteerSign: signatureUrl,
      Language: row.cl_primarylanguage || "N/A",
    };
  };

  const mappedPreviewHtml = useMemo(() => {
    if (!templateHtml || !currentSubject) return null;

    const mappedData = mapSubjectToData(currentSubject);

    let renderedHtml = replacePlaceholders(templateHtml, mappedData);
    renderedHtml = DOMPurify.sanitize(renderedHtml, {
      ADD_TAGS: ["style"],
      WHOLE_DOCUMENT: true,
      FORCE_BODY: true,
    });

    return renderedHtml.replace("___BARCODE_MARKER___", mappedData.BarcodeHtml);
  }, [templateHtml, currentSubject, selectedStudyLabel]);

  // Barcode Rendering Effect - useLayoutEffect matching SubjectLabel & VacutainerLabel
  useLayoutEffect(() => {
    if (previewRef.current && currentSubject) {
      const targets = previewRef.current?.querySelectorAll(".barcode-target");
      targets?.forEach((target) => {
        const val = target.getAttribute("data-barcode") || "";

        if (target.children.length === 0) {
          const root = createRoot(target as HTMLElement);
          root.render(
            <Barcode
              value={val}
              format="CODE128"
              width={1.5}
              height={36}
              displayValue={true}
              fontSize={12}
              margin={0}
            />,
          );
        }
      });
    }
  }, [mappedPreviewHtml, currentSubject, selectedRows, currentIndex, isFlipped]);

  const handlePrint = () => {
    if (!templateHtml || selectedRows.length === 0) return;

    let printHtml = "";
    selectedRows.forEach((row) => {
      const mappedData = mapSubjectToData(row);
      let renderedHtml = replacePlaceholders(templateHtml, mappedData);

      renderedHtml = DOMPurify.sanitize(renderedHtml, {
        ADD_TAGS: ["style"],
        WHOLE_DOCUMENT: true,
        FORCE_BODY: true,
      });

      renderedHtml = renderedHtml.replace(
        "___BARCODE_MARKER___",
        mappedData.BarcodeHtml,
      );
      printHtml += `<div class="mb-4 page-break label-wrapper">${renderedHtml}</div>`;
    });

    const newWindow = window.open("", "_blank");
    if (!newWindow) return;

    const style = `<style>
                body { 
                    font-family: Arial, sans-serif; 
                    margin: 0;
                    padding: 0;
                    -webkit-print-color-adjust: exact !important;
                    print-color-adjust: exact !important;
                }
                .page-break { 
                    page-break-after: always; 
                }
                .label-wrapper {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    width: 100%;
                    padding: 30px 0;
                }
                @media print {
                    @page { margin: 0; }
                    .label-wrapper {
                        padding: 30px 0;
                    }
                }
            </style>`;

    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = printHtml;
    document.body.appendChild(tempDiv);

    const targets = tempDiv.querySelectorAll(".barcode-target");
    targets.forEach((target) => {
      target.innerHTML = "";
      const val = target.getAttribute("data-barcode") || "123456789";
      const root = createRoot(target as HTMLElement);
      root.render(
        <Barcode
          value={val}
          format="CODE128"
          width={1.5}
          height={36}
          displayValue={true}
          fontSize={12}
          margin={0}
        />,
      );
    });

    setTimeout(() => {
      newWindow.document.write(style + tempDiv.innerHTML);
      newWindow.document.close();
      newWindow.focus();

      setTimeout(() => {
        newWindow.print();
        newWindow.close();
        document.body.removeChild(tempDiv);

        const payload = selectedRows
          .filter((s) => s.volunteerassignmentid)
          .map((s) => ({
            id: s.volunteerassignmentid,
            quantity: 1,
          }));

        if (payload.length > 0) {
          UpdateLabelPrintMutation.mutate(
            {
              volunteerassignment: payload,
              printtype: "IdCard Label",
              studyid: selectedStudyId ? Number(selectedStudyId) : null,
            },
            {
              onSuccess: (res: any) => {
                showToastMessage(
                  "success",
                  res.message || "Updated Successfully",
                );
                queryClient.invalidateQueries({
                  queryKey: ["volunteer-enrollment-selectAll"],
                });
                setSelectedRows([]);
              },
              onError: (err: any) => {
                showToastMessage(
                  "error",
                  err.message || "Failed to update print status",
                );
              },
            },
          );
        }
      }, 500);
    }, 300);
  };
  const handleSelectAll = (isChecked: boolean) => {
    if (isChecked) {
      setSelectedRows(volunteersData);
    } else {
      setSelectedRows([]);
    }
    setCurrentIndex(0);
    setIsFlipped(false);
  };

  const isAllSelected =
    volunteersData.length > 0 &&
    volunteersData.every((v: any) =>
      selectedAssignmentIds.includes(v.volunteerassignmentid),
    );

  return (
    <Row>
      {/* Left Column - Form & Table */}
      <Col lg={7}>
        <div className="mb-4">
          <CommonForm
            fields={ICardFields(studyOptions)}
            control={control}
            errors={errors}
            columns={2}
          />
        </div>

        {selectedStudyId && (
          <div className="mt-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6
                className="fw-700 mb-0 small text-uppercase ls-1"
                style={{ color: "#008080" }}
              >
                Enrolled Volunteers
              </h6>
              <div
                className={`${sf.searchBox} ${searchFocused ? sf.focused : ""}`}
                style={{ width: "250px", marginBottom: 0 }}
              >
                <i className={`ri-search-line ${sf.searchIcon}`} />
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchText}
                  className={sf.searchInput}
                  onChange={(e) => setSearchText(e.target.value)}
                  onFocus={() => setSearchFocused(true)}
                  onBlur={() => setSearchFocused(false)}
                />
                {searchText && (
                  <i
                    className={`ri-close-line ${sf.clearIcon}`}
                    onClick={() => setSearchText("")}
                  />
                )}
              </div>
            </div>
            <CommonTableIndexWise
              columns={ICardColumns(
                selectedAssignmentIds,
                toggleSelection,
                isAllSelected,
                handleSelectAll
              )}
              data={volunteersData}
              className={tableStyles.table}
              hideAction={true}
              maxHeight="400px"
              stickyHeader={true}
              loading={
                volunteersQuery.isLoading || volunteersQuery.isFetching
              }
              totalRecords={totalRecords}
              paginationShow={false}
            />
          </div>
        )}
      </Col>

      {/* Right Column - Preview */}
      {selectedStudyId && (
        <Col lg={5}>
          <Card className="border shadow-none rounded-4 overflow-hidden">
            <CardBody className="p-3 d-flex flex-column">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <h6 className="fw-700 mb-1">ICard Preview</h6>
                  <p className="text-muted extra-small mb-0">
                    Standard PVC Card Size (85.60 x 53.98 mm)
                  </p>
                </div>
                {currentSubject && (
                  <Button
                    color="outline-primary"
                    size="sm"
                    className="rounded-3 py-1 px-2 extra-small fw-semibold"
                    onClick={() => setIsFlipped(!isFlipped)}
                  >
                    <i className="ri-refresh-line me-1" />
                    {isFlipped ? "Show Front" : "Show Back"}
                  </Button>
                )}
              </div>

              <div className={styles.preview_container} ref={previewRef}>
                {isTemplateLoading ? (
                  <div className="text-center py-4">
                    <Spinner size="sm" color="primary" />
                  </div>
                ) : currentSubject ? (
                  <div
                    className="flip-card-container-wrapper d-flex flex-column align-items-center w-100"
                    style={{ minHeight: "330px" }}
                  >
                    <div
                      className={`${styles.flip_card_container} ${isFlipped ? styles.flipped : ''}`}
                      onClick={() => setIsFlipped(!isFlipped)}
                    >
                      <div className={styles.flip_card_inner}>
                        <div
                          className="preview-mode"
                          dangerouslySetInnerHTML={{
                            __html: mappedPreviewHtml || "",
                          }}
                        />
                      </div>
                    </div>
                    <div className="text-center text-muted extra-small mt-2">
                      <i className="ri-information-line me-1" />
                      Click the card to flip between front and back
                    </div>
                  </div>
                ) : (
                  <div className="text-center text-muted p-4 border rounded-3 bg-white w-100">
                    <i className="ri-id-card-line fs-1 d-block mb-2 opacity-25" />
                    <p className="small mb-0">
                      Select a subject from the list to preview their ICard
                    </p>
                  </div>
                )}
              </div>

              {previewList.length > 1 && (
                <div className="mt-3">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <Button
                      color="light"
                      size="sm"
                      className="rounded-3 p-0 d-flex align-items-center justify-content-center border"
                      style={{
                        width: "32px",
                        height: "32px",
                        backgroundColor: "#fff",
                      }}
                      onClick={() =>
                        setCurrentIndex(Math.max(0, currentIndex - 1))
                      }
                      disabled={currentIndex === 0}
                    >
                      <i className="ri-arrow-left-s-line fs-5" />
                    </Button>

                    <div className="px-3 py-1 bg-light rounded-3">
                      <span className="fw-700 small text-primary">
                        {currentIndex + 1} of {previewList.length}
                      </span>
                    </div>

                    <Button
                      color="light"
                      size="sm"
                      className="rounded-3 p-0 d-flex align-items-center justify-content-center border"
                      style={{
                        width: "32px",
                        height: "32px",
                        backgroundColor: "#fff",
                      }}
                      onClick={() =>
                        setCurrentIndex(
                          Math.min(previewList.length - 1, currentIndex + 1),
                        )
                      }
                      disabled={currentIndex === previewList.length - 1}
                    >
                      <i className="ri-arrow-right-s-line fs-5" />
                    </Button>
                  </div>
                </div>
              )}

              <CommonButton
                module={PERMISSION_MODULES.SUBJECT_ICARD_LABEL}
                action={PERMISSION_ACTIONS.PRINT}
                color="primary"
                block
                className="py-2 rounded-3 d-flex align-items-center justify-content-center gap-2 fw-700 shadow-sm mt-3"
                disabled={previewList.length === 0 || UpdateLabelPrintMutation.isPending}
                onClick={handlePrint}
              >
                {UpdateLabelPrintMutation.isPending ? (
                  <Spinner size="sm" />
                ) : (
                  <i className="ri-printer-line" />
                )}{" "}
                Print ICards
              </CommonButton>
            </CardBody>
          </Card>
        </Col>
      )}
    </Row>
  );
};

export default SubjectICardList;
